import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { Migration } from '../../../types';
import { getScheduleDisplay } from '../../../utils/cron';
import { formatUtcTimestampLocal } from '../../../utils/formatters';
import { ui } from '../../../config/uiSizes';

interface AttentionModalContentProps {
  job: Migration;
  onClose?: () => void;
}

export const AttentionModalContent: React.FC<AttentionModalContentProps> = ({ job, onClose: _onClose }) => {
  const navigate = useNavigate();
  const scheduleDisplay = getScheduleDisplay(job.snapshotCronExpression);
  const [isCopied, setIsCopied] = React.useState(false);

  // Determine which job has errors and the appropriate action
  const getErrorInfo = () => {
    // Covers both explicit 'ImportFailed'/'ValidationFailed' and the generic 'Failed'
    // status the backend emits when an import job errors out.
    const isImportFailure =
      !job.isSnapshot &&
      (
        job.status === 'ImportFailed' ||
        job.status === 'ValidationFailed' ||
        (job.status === 'Failed' && (job.job3Import?.errors?.length ?? 0) > 0)
      );

    if (isImportFailure) {
      const errors = (job.job3Import?.errors?.length ?? 0) > 0
        ? job.job3Import!.errors
        : (job.job1Export?.errors?.length ?? 0) > 0
          ? job.job1Export!.errors
          : (job.job2Transform?.errors?.length ?? 0) > 0
            ? job.job2Transform!.errors
            : [];

      return {
        stage: 'Import',
        errors,
        buttonText: 'Go to Approve Export',
        route: '/ApproveExportPage',
        state: { job }
      };
    }
    if (job.job1Export?.errors && job.job1Export.errors.length > 0 && job.isSnapshot) {
      // Snapshots cannot be edited, only stopped/resumed from the dashboard
      return {
        stage: 'Export',
        errors: job.job1Export.errors,
        buttonText: null,
        route: null,
        navigateHandler: null
      };
    } else if (job.job1Export?.errors && job.job1Export.errors.length > 0 && !job.isSnapshot) {
      return {
        stage: 'Export',
        errors: job.job1Export.errors,
        buttonText: 'Edit Migration Settings',
        route: '/MigrationSetup',
        state: {
          migrationData: {
            name: job.name,
            sourceOrgName: job.sourceOrgName,
            targetOrgName: job.targetOrgName,
            sourceOrgId: job.sourceOrgId,
            targetOrgId: job.targetOrgId,
            resources: job.resources,
            id: job.id,
            isPrePopulatedForm: true
          }
        }
      };
    }

    if (job.job2Transform?.errors && job.job2Transform.errors.length > 0) {
      return {
        stage: 'Transformation',
        errors: job.job2Transform.errors,
        buttonText: 'Go to Transformation Page',
        route: '/ApproveTransformationsPage',
        state: { job }
      };
    }
    if (job.job3Import?.errors && job.job3Import.errors.length > 0) {
      return {
        stage: 'Import',
        errors: job.job3Import.errors,
        buttonText: 'Go to Approve Export',
        route: '/ApproveExportPage',
        state: { job }
      };
    }
    return null;
  };

  const errorInfo = getErrorInfo();

  const typeLabel = job.isSnapshot ? 'Snapshot' : 'Migration';

  const handleCopyErrorLog = async () => {
    if (!errorInfo) return;

    const errorText = Array.isArray(errorInfo.errors)
      ? errorInfo.errors.join('\n')
      : errorInfo.errors;

    if (!errorText) return;

    try {
      await navigator.clipboard.writeText(errorText);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy error log:', err);
    }
  };

  const handleNavigateToFix = () => {
    if (!errorInfo?.route) return;

    navigate(errorInfo.route, errorInfo.state ? { state: errorInfo.state } : undefined);
  };

  // --- Status Progress Tracker ---
  type StepStatus = 'completed' | 'current' | 'failed' | 'approval' | 'pending';

  interface ProgressStep {
    label: string;
    subLabel?: string | undefined;
    description: string;
    status: StepStatus;
  }

  const getMigrationSteps = (): ProgressStep[] => {
    const incomingStatus = job.status.toString();
    const failedStage = errorInfo?.stage;

    const exportStatus: StepStatus =
      failedStage === 'Export'
        ? 'failed'
        : incomingStatus === 'ExportFailed'
          ? 'failed'
          : ['ExportQueued', 'ExportInProgress'].includes(incomingStatus)
            ? 'current'
            : incomingStatus === 'ExportComplete'
              ? 'approval'
              // If a later stage failed, export must have completed successfully
              : (failedStage === 'Transformation' || failedStage === 'Import')
                ? 'completed'
                : [
                  'TransformationQueued',
                  'TransformationInProgress',
                  'TransformationComplete',
                  'TransformationFailed',
                  'ImportQueued',
                  'ImportInProgress',
                  'ImportFailed',
                  'ValidationFailed',
                  'Failed',
                  'completed',
                  'PartiallyCompleted'
                ].includes(incomingStatus)
                  ? 'completed'
                  : 'pending';

    const transformStatus: StepStatus =
      failedStage === 'Transformation'
        ? 'failed'
        : incomingStatus === 'TransformationFailed'
          ? 'failed'
          : exportStatus === 'failed'
            ? 'pending'
            : ['TransformationQueued', 'TransformationInProgress'].includes(incomingStatus)
              ? 'current'
              : incomingStatus === 'TransformationComplete'
                ? 'approval'
                // If import stage failed, transform must have completed (or been skipped)
                : failedStage === 'Import'
                  ? 'completed'
                  : [
                    'ImportQueued',
                    'ImportInProgress',
                    'ImportFailed',
                    'ValidationFailed',
                    'Failed',
                    'completed',
                    'PartiallyCompleted'
                  ].includes(incomingStatus)
                    ? 'completed'
                    : 'pending';

    const importStatus: StepStatus =
      failedStage === 'Import'
        ? 'failed'
        : incomingStatus === 'ImportFailed' || incomingStatus === 'ValidationFailed' || incomingStatus === 'Failed'
          ? 'failed'
          : exportStatus === 'failed' || transformStatus === 'failed'
            ? 'pending'
            : ['ImportQueued', 'ImportInProgress'].includes(incomingStatus)
              ? 'current'
              : ['completed', 'PartiallyCompleted'].includes(incomingStatus)
                ? 'completed'
                : 'pending';

    const completeStatus: StepStatus =
      exportStatus === 'failed' || transformStatus === 'failed' || importStatus === 'failed'
        ? 'pending'
        : incomingStatus === 'PartiallyCompleted'
          ? 'current'
          : incomingStatus === 'completed'
            ? 'completed'
            : 'pending';

    return [
      {
        label: 'Export',
        description:
          exportStatus === 'failed'
            ? 'Export encountered errors'
            : exportStatus === 'completed'
              ? 'Resources exported successfully'
              : 'Resources will be exported from the source organization',
        status: exportStatus
      },
      {
        label: 'Transform',
        description:
          transformStatus === 'failed'
            ? 'Transformation encountered errors'
            : transformStatus === 'completed'
              ? 'Transformations applied successfully'
              : 'Transformations will be applied to exported resources',
        status: transformStatus
      },
      {
        label: 'Import',
        description:
          importStatus === 'failed'
            ? 'Import encountered errors'
            : importStatus === 'completed'
              ? 'Resources imported successfully'
              : 'Resources will be imported into the target organization',
        status: importStatus
      },
      {
        label: 'Complete',
        subLabel: completeStatus === 'current' ? 'Partial' : undefined,
        description:
          completeStatus === 'completed'
            ? 'Migration completed successfully'
            : completeStatus === 'current'
              ? 'Migration partially completed'
              : 'Migration will be marked complete',
        status: completeStatus
      }
    ];
  };

  const getSnapshotSteps = (): ProgressStep[] => {
    const incomingStatus = job.status.toString();
    const failedStage = errorInfo?.stage;

    const snapshotExportStatus: StepStatus =
      failedStage === 'Export'
        ? 'failed'
        : incomingStatus === 'ExportFailed'
          ? 'failed'
          : ['ExportQueued', 'ExportInProgress'].includes(incomingStatus)
            ? 'current'
            : incomingStatus === 'SnapshotCompleted'
              ? 'completed'
              : 'pending';

    const completeStatus: StepStatus =
      snapshotExportStatus === 'failed'
        ? 'pending'
        : incomingStatus === 'SnapshotCompleted'
          ? 'completed'
          : 'pending';

    return [
      {
        label: 'Export',
        description:
          snapshotExportStatus === 'failed'
            ? 'Snapshot encountered errors'
            : snapshotExportStatus === 'completed'
              ? 'Resources captured from the source organization'
              : 'Resources will be captured from the source organization',
        status: snapshotExportStatus
      },
      {
        label: 'Complete',
        description:
          completeStatus === 'completed'
            ? 'Snapshot completed successfully'
            : 'Snapshot will be marked complete once all resources are captured',
        status: completeStatus
      }
    ];
  };

  const progressSteps = job.isSnapshot ? getSnapshotSteps() : getMigrationSteps();

  const stepBg: Record<StepStatus, string> = {
    completed: 'bg-green-500 dark:bg-green-600',
    current: 'bg-blue-500 dark:bg-blue-600',
    failed: 'bg-red-500 dark:bg-red-600',
    approval: 'bg-orange-500 dark:bg-orange-600',
    pending: 'bg-gray-300 dark:bg-gray-600'
  };

  const stepIcon = (status: StepStatus, index: number) => {
    if (status === 'completed')
      return (
        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z"
            clipRule="evenodd"
          />
        </svg>
      );
    if (status === 'failed')
      return (
        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      );
    if (status === 'approval')
      return (
        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
            clipRule="evenodd"
          />
        </svg>
      );
    if (status === 'current')
      return <span className="w-2 h-2 rounded-full bg-white animate-pulse block" />;
    return <span className="text-xs font-bold text-gray-500 dark:text-gray-400">{index + 1}</span>;
  };

  return (
    <div className="space-y-6">
      {/* Status Header */}
      <div
        className={`rounded-lg ${ui.page.cardPadding} ${errorInfo ? 'bg-red-100 dark:bg-red-900/30 border-2 border-red-400 dark:border-red-600' : 'bg-green-100 dark:bg-green-900/30 border-2 border-green-400 dark:border-green-600'}`}
      >
        <div className="flex items-center justify-between">
          <div className={`flex items-center ${ui.gap.lg}`}>
            <div
              className={`${ui.svg.xl} rounded-full flex items-center justify-center ${errorInfo ? 'bg-red-500' : 'bg-green-500'}`}
            >
              <svg className={`${ui.svg.lg} text-white`} fill="currentColor" viewBox="0 0 20 20">
                {errorInfo ? (
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                ) : (
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                )}
              </svg>
            </div>
            <div>
              <h3
                className={`font-black ${ui.text.sectionTitle} ${errorInfo ? 'text-red-900 dark:text-red-200' : 'text-green-900 dark:text-green-200'}`}
              >
                {errorInfo ? `${errorInfo.stage} Failed` : 'Migration Details'}
              </h3>
              <p
                className={`${ui.text.label} ${errorInfo ? 'text-red-700 dark:text-red-300' : 'text-green-700 dark:text-green-300'}`}
              >
                {errorInfo ? 'Requires attention' : 'All systems operational'}
              </p>
            </div>
          </div>
          {errorInfo && (
            <span className={`bg-red-600 text-white ${ui.button.sm} rounded-full font-bold`}>
              ERROR
            </span>
          )}
        </div>
      </div>

      {/* Info Cards Grid */}
      <div className="space-y-4">
        {/* Hero Section - Name & Status */}
        <div
          className={`bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 ${ui.page.cardPadding}`}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div
                className={`${ui.text.label} font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${ui.mb.sm}`}
              >
                {typeLabel} Name
              </div>
              <div
                className={`${ui.text.sectionTitle} font-bold text-gray-900 dark:text-gray-100 break-words`}
              >
                {job.name}
              </div>
            </div>
          </div>
        </div>

        {/* Progress Tracker */}
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <div
            className={`${ui.text.label} font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3`}
          >
            {typeLabel} Progress
          </div>
          <div className="relative flex items-start gap-0">
            {progressSteps.map((step, index) => (
              <div key={step.label} className="flex-1 flex flex-col items-center relative">
                {/* Connector line */}
                {index < progressSteps.length - 1 && (
                  <div
                    className={`absolute top-3.5 left-1/2 w-full h-0.5 z-0 ${step.status === 'completed' ? 'bg-green-400 dark:bg-green-600' : step.status === 'failed' ? 'bg-red-400 dark:bg-red-600' : 'bg-gray-300 dark:bg-gray-600'}`}
                  />
                )}

                {/* Circle */}
                <div
                  className={`relative z-10 w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${stepBg[step.status]} ${step.status === 'current' ? 'ring-2 ring-offset-2 ring-blue-400 dark:ring-offset-gray-800' : ''} ${step.status === 'failed' ? 'ring-2 ring-offset-2 ring-red-400 dark:ring-offset-gray-800' : ''}`}
                >
                  {stepIcon(step.status, index)}
                </div>

                {/* Label + description */}
                <div className="mt-2 text-center px-1">
                  <div className="flex items-center justify-center gap-1 flex-wrap">
                    <div
                      className={`${ui.text.label} font-semibold ${step.status === 'completed'
                        ? 'text-green-700 dark:text-green-400'
                        : step.status === 'current'
                          ? 'text-blue-700 dark:text-blue-300'
                          : step.status === 'failed'
                            ? 'text-red-600 dark:text-red-400'
                            : step.status === 'approval'
                              ? 'text-orange-600 dark:text-orange-400'
                              : 'text-gray-400 dark:text-gray-500'
                        }`}
                    >
                      {step.label}
                    </div>
                    {step.subLabel && (
                      <span
                        className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none ${step.subLabel === 'Queued'
                          ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300'
                          : step.subLabel === 'Partial'
                            ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                          }`}
                      >
                        {step.subLabel}
                      </span>
                    )}
                  </div>
                  <div
                    className={`${ui.text.label} text-gray-500 dark:text-gray-400 mt-0.5 leading-tight`}
                  >
                    {step.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Schedule Display */}
        {scheduleDisplay && (
          <div
            className={`flex items-center ${ui.gap.lg} ${ui.page.sectionPadding} bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-700`}
          >
            <div className="p-2 bg-blue-100 dark:bg-blue-800/50 rounded-lg">
              <svg
                className={`${ui.svg.sm} text-blue-600 dark:text-blue-400`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <div className={`${ui.text.label} font-medium text-blue-700 dark:text-blue-300 mb-1`}>
                Recurring Schedule
              </div>
              <div className={`font-bold ${ui.text.label} text-blue-900 dark:text-blue-100`}>
                {scheduleDisplay}
              </div>
            </div>
          </div>
        )}

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <svg
                className={`${ui.svg.sm} text-blue-600 dark:text-blue-400`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                <path
                  fillRule="evenodd"
                  d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className={`${ui.text.label} font-medium text-gray-500 dark:text-gray-400 mb-1`}>
                Type
              </div>
              <div className={`font-semibold ${ui.text.label} text-gray-900 dark:text-gray-100`}>
                {typeLabel}
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <svg
                className={`${ui.svg.sm} text-purple-600 dark:text-purple-400`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className={`${ui.text.label} font-medium text-gray-500 dark:text-gray-400 mb-1`}>
                Created By
              </div>
              <div
                className={`font-semibold ${ui.text.label} text-gray-900 dark:text-gray-100 md:truncate xl:whitespace-normal xl:overflow-visible`}
                title={job.createdBy}
              >
                {job.createdBy}
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <svg
                className={`${ui.svg.sm} text-orange-600 dark:text-orange-400`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className={`${ui.text.label} font-medium text-gray-500 dark:text-gray-400 mb-1`}>
                Created At
              </div>
              <div className={`font-semibold ${ui.text.label} text-gray-900 dark:text-gray-100`}>
                {formatUtcTimestampLocal(job.createdAtUtc)}
              </div>
            </div>
          </div>

          {job.updatedAtUtc && (
            <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                <svg
                  className={`${ui.svg.sm} text-teal-600 dark:text-teal-400`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div
                  className={`${ui.text.label} font-medium text-gray-500 dark:text-gray-400 mb-1`}
                >
                  Last Updated
                </div>
                <div className={`font-semibold ${ui.text.label} text-gray-900 dark:text-gray-100`}>
                  {formatUtcTimestampLocal(job.updatedAtUtc)}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error Message Area */}
      {errorInfo && (
        <>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <svg
                className={`${ui.svg.sm} text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="flex-1">
                <h4
                  className={`${ui.text.label} font-bold text-yellow-800 dark:text-yellow-300 mb-1`}
                >
                  Action Required
                </h4>
                <p className={`${ui.text.body} text-yellow-700 dark:text-yellow-400`}>
                  {errorInfo.buttonText ? (
                    <>
                      The {errorInfo.stage.toLowerCase()} stage encountered errors. Please click the
                      <span className="font-semibold"> {errorInfo.buttonText} button</span> below to
                      navigate back to the previous step and resolve these issues before proceeding.
                    </>
                  ) : (
                    <>
                      The {errorInfo.stage.toLowerCase()} stage encountered errors. Review the error
                      log below for details.
                      {job.snapshotCronExpression && (
                        <span>
                          {' '}
                          If this is a recurring snapshot, you can stop it using the{' '}
                          <span className="font-semibold">Stop Snapshot</span> button in the
                          dashboard.
                        </span>
                      )}
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
          {errorInfo.buttonText && (
            <button
              onClick={handleNavigateToFix}
              className={`w-full bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 text-white font-semibold ${ui.button.lg} rounded-lg transition shadow-lg flex items-center justify-center gap-2`}
            >
              <svg
                className={`${ui.svg.sm}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              <span>{errorInfo.buttonText}</span>
            </button>
          )}
          <div className="bg-black dark:bg-black rounded-lg p-4 border border-gray-700">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-2 mb-3">
              <span className={`${ui.text.label} font-bold text-red-400 uppercase tracking-wider flex-shrink-0`}>
                Error Log
              </span>
              <div className="hidden sm:block flex-1 h-px bg-red-900"></div>
              <button
                onClick={handleCopyErrorLog}
                className={`w-full sm:w-auto flex items-center justify-center gap-2 rounded-lg font-semibold transition-all duration-300 shadow-lg ${ui.button.copyError} ${isCopied
                  ? 'bg-green-500 hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700 text-white scale-105'
                  : 'bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 text-white hover:scale-105'
                  }`}
                title={isCopied ? 'Copied!' : 'Copy error log to clipboard'}
              >
                {isCopied ? (
                  <>
                    <svg
                      className={`${ui.svg.copyError} animate-bounce`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="font-bold">Copied!</span>
                  </>
                ) : (
                  <>
                    <svg
                      className={ui.svg.copyError}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    <span>Copy Error</span>
                  </>
                )}
              </button>
            </div>
            <pre className={`font-mono whitespace-pre-wrap break-words ${ui.widthAndHeight.errorLog} overflow-y-auto ${ui.text.label} text-green-400`} >
              {Array.isArray(errorInfo.errors) ? errorInfo.errors.join('\n') : errorInfo.errors}
            </pre>
          </div>
        </>
      )}
    </div>
  );
};
